import { DetailedDailyDowntimeItem } from './DetailedDailyTypes';
import { ReactComponent as PencilIcon } from '../../../images/icon-pencil.svg';
import { ReactComponent as TrashIcon } from '../../../images/trash.svg';
import useConfig from '../../../hooks/useConfig';
import { useEffect, useMemo, useState } from 'react';
import ExportTable from '../../../components/common/export-table/ExportTable';
import ColumnSelector from '../../../components/column-selector/colum-selector';
import DataTable from './DataTable';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import { formatDate } from '../../../utilities/dateHelpers';
import { DashboardConfig, saveUserPerferences } from '../../../dispatchers/UserPreferenceDispatcher';
import TableSearch from '../../../components/table-search/TableSearch';
import Tooltip from '../../common/tooltip/ToolTip';

interface DetailedDailyTableProps {
  data: DetailedDailyDowntimeItem[];
  handleEdit: (rowData: DetailedDailyDowntimeItem) => void;
  handleDelete: (id: number) => void;
  onAddClick: () => void;
}

const ACRONYMS = ['MOP', 'ESP', 'BHP', 'GLR', 'GOR', 'THP', 'BHT', 'VFD'];

const toSentenceCasePreserveAcronyms = (label?: string | null) => {
  if (!label) return '';
  const trimmed = String(label).trim();
  const upper = trimmed.toUpperCase();
  if (ACRONYMS.includes(upper)) return upper;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

const DetailedDailyTable: React.FC<DetailedDailyTableProps> = ({ data, handleEdit, handleDelete, onAddClick }) => {
  const { enablePumpChecker } = useConfig();
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [isConfigCheckBoxClicked, setIsConfigCheckBoxClicked] = useState(false);
  const downtimePageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const downtimeWidget = downtimePageData?.find((obj: any) => obj.key === 'downtime-detailtable');
  const dispatch = useAppDispatch();
  const [, setApplyTrigger] = useState(false);
  const [allColumnsForToolbar, setAllColumnsForToolbar] = useState<string[]>([]);
  const [fixedColumns, setFixedColumns] = useState<string[]>([]);
  const [hasColumnsReset, setHasColumnsReset] = useState(false);
  const [hasReset, setHasReset] = useState(false);
  const [isResetClicked, setIsResetClicked] = useState(false);
  const industryApplicationId = useAppSelector((state: any) => state.AssetAnalysis.industryApplicationId);
  const isGLOrEsp = [4, 7].includes(industryApplicationId);
  const fixedColumnDefs = [
    ...(enablePumpChecker
      ? [
        {
          Header: '',
          id: 'downtime-actions',
          width: 80,
          Filter: () => <></>,
          Cell: ({ row }: { row: { original: DetailedDailyDowntimeItem } }) => {
            if (row.original.Separated) return <div className='tdt-action-cell h-[38px]'></div>;
            return (
              <div className='tdt-action-cell'>
                {isGLOrEsp && (
                  <>
                    <PencilIcon onClick={() => handleEdit(row.original)} />
                    <TrashIcon
                      onClick={() => handleDelete(row.original.Id)}
                      style={{
                        cursor: 'pointer',
                      }}
                    />
                  </>
                )}
                {!isGLOrEsp && (
                  <>
                    <Tooltip content='Feature not supported' className='not-supported' direction='right'>
                      <PencilIcon className='pointer-events-not-allowed' />
                      <TrashIcon className='pointer-events-not-allowed' />
                    </Tooltip>
                  </>
                )}
              </div>
            );
          },
        },
      ]
      : []),
  ];
  const columnDefs: any[] = useMemo(() => {
    const props = downtimeWidget?.widgetProperty?.widgetProperties || {};
    const cols = props?.columns || [];
    return cols.map((c: any) => ({
      Header: toSentenceCasePreserveAcronyms(c.label),
      accessor: c.key,
      Cell: ({ value }: { value: any }) => {
        if (c.key === 'IndividualDateLocalString' || c.key === 'IndividualEndDateLocalString') {
          return <span>{formatDate(value, true)}</span>;
        }
        return <span>{value ?? '-'}</span>;
      },
    }));
  }, [downtimeWidget?.widgetProperty]);
  const getColumnHeader = (columnId: string) => {
    const col = columnDefs.find((col: any) => col.accessor === columnId);
    return toSentenceCasePreserveAcronyms(col?.Header || columnId);
  };
  // Prepare columns for ColumnSelector
  const columnsTransferred: {
    id: string;
    Header: string | React.ReactNode;
    isVisible: boolean;
  }[] = allColumnsForToolbar
    .filter((id) => !fixedColumns.includes(id))
    .map((id) => ({
      id,
      Header: getColumnHeader(id),
      isVisible: visibleColumns.includes(id),
    }));
  const [localColumns, setLocalColumns] = useState(() =>
    columnsTransferred.map((col) => ({
      id: col.id,
      Header: typeof col.Header === 'string' ? col.Header : '',
      isVisible: col.isVisible,
      key: col.id,
    })),
  );

  const exportColumns = useMemo(
    () =>
      columnDefs
        .filter((col) => visibleColumns.includes(col.accessor))
        .map((col) => ({
          key: typeof col.accessor === 'string' ? col.accessor : '',
          header: typeof col.Header === 'string' ? col.Header : '',
          accessor: (row: { original: DetailedDailyDowntimeItem }) => {
            const key = typeof col.accessor === 'string' ? col.accessor : '';
            const value = row.original?.[key as keyof DetailedDailyDowntimeItem] ?? '';

            if (key === 'IndividualDateLocalString' || key === 'IndividualEndDateLocalString') {
              return value ? formatDate(String(value), true) : '';
            }

            return String(value);
          },
        })),
    [columnDefs, visibleColumns],
  );
  const tableRowsForExport = Array.isArray(data) ? data.map((item: any) => ({ original: item })) : [];
  const rPermission = { canExport: true };

  const handleSaveConfig = (config: boolean) => {
    setIsConfigCheckBoxClicked(config);
  };
  const resetHandler = () => {
    const props = downtimeWidget?.widgetProperty?.widgetProperties || {};

    if (!props?.columns?.length) return;

    setIsResetClicked(true);
    const configurableColumnsAfterReset = props.columns.map((col: any) => col.key);
    const visibleColumnsAfterReset = [...configurableColumnsAfterReset];

    setAllColumnsForToolbar(configurableColumnsAfterReset);
    setVisibleColumns(visibleColumnsAfterReset);

    setTimeout(() => {
      setIsResetClicked(false);
    }, 3000);
  };
  const handleApplyClicked = async () => {
    const mandatoryColumns = (downtimeWidget?.widgetProperty?.widgetProperties?.fixedColumns || []).map(
      (c: any) => c.id || c.label,
    );

    // Get currently selected columns from localColumns (which reflects current ColumnSelector state)
    const selectedColumns = localColumns.filter((col) => col.isVisible).map((col) => col.id);
    const newVisibleColumns = [...selectedColumns.filter((col) => !mandatoryColumns.includes(col))];

    // Update visible columns immediately to reflect changes in the table
    setVisibleColumns(newVisibleColumns);

    // Only include configurable columns in the transformation (exclude mandatory columns)
    const configurableColumns = localColumns.filter((item) => !mandatoryColumns.includes(item.id));
    const transformedArray = configurableColumns.map((item, index) => ({
      order: index + 1,
      label: item.Header,
      key: item.id,
      selected: item.isVisible,
    }));

    if (isConfigCheckBoxClicked) {
      try {
        const transformedWidget: DashboardConfig = {
          DashboardName: downtimeWidget?.widgetProperty?.dashboard ?? '',
          WidgetName: downtimeWidget?.widgetProperty?.key ?? 'downtime-detailtable',
          PropertyType: 'Columns',
          Preferences: transformedArray.map((col) => ({
            Order: col.order,
            Label: col.label,
            Key: col.key,
            Selected: col.selected,
            Visible: true,
          })),
        };

        const resultAction = await dispatch(saveUserPerferences(transformedWidget));
        if (typeof resultAction.payload === 'boolean') {
          if (resultAction.payload) {
            console.log('Column configuration saved successfully');
          } else {
            console.error('Failed to save configuration. Please try again.');
          }
        }
      } catch (error) {
        console.error('Error saving column configuration:', error);
      }
    }

    setApplyTrigger(true);
    setTimeout(() => setApplyTrigger(false), 300);
  };

  useEffect(() => {
    const props = downtimeWidget?.widgetProperty?.widgetProperties || {};

    const fixed = [...(props.fixedColumns?.map((col: any) => col.key || col.label) || [])];

    // Get visible columns from config, but ensure mandatory columns are always included
    const configuredVisible = props.columns?.filter((col: any) => col.selected).map((col: any) => col.key) || [];
    const visible = [...configuredVisible];

    // All columns for toolbar should exclude the mandatory columns (they won't appear in column selector)
    const allConfigurable = props.columns?.map((col: any) => col.key) || columnDefs.map((col) => col.accessor);

    setFixedColumns(fixed);
    setVisibleColumns(visible);
    setAllColumnsForToolbar(allConfigurable);
    setApplyTrigger(true);
    setHasColumnsReset(true);
    setTimeout(() => setApplyTrigger(false), 300);
  }, [downtimeWidget]);
  useEffect(() => {
    if (hasReset || !localColumns.length || hasColumnsReset) {
      setLocalColumns(
        columnsTransferred.map((col) => ({
          id: col.id,
          Header: typeof col.Header === 'string' ? col.Header : '',
          isVisible: col.isVisible,
          key: col.id,
        })),
      );
      setHasReset(false);
      setHasColumnsReset(false);
    }
  }, [hasReset, columnsTransferred, isResetClicked, hasColumnsReset]);

  return (
    <>
      <div className='detailed-daily-toolbar'>
        {/* Column selector flyout */}
        {showColumnSelector && (
          <ColumnSelector
            columnsData={localColumns}
            onUpdateColumns={(updated) => {
              setLocalColumns(updated);
            }}
            onClose={() => setShowColumnSelector(false)}
            onApplyClickedInColumn={handleApplyClicked}
            saveConfigHasbeenClicked={handleSaveConfig}
            resetToDefaultSystemClicked={() => {
              resetHandler();
              setHasReset(true);
            }}
            tableName='downtime-detailtable'
            isChartConfig={false}
          />
        )}
        {!isGLOrEsp && (
          <Tooltip content='Feature not supported' className='not-supported' direction='right'>
            <button
              title='Feature not supported'
              className={`pointer-events-not-allowed flex w-auto h-[40px] pt-[10px] pr-[16px] pb-[10px] pl-[16px] gap-[8px] items-center shrink-0 flex-nowrap rounded-[8px] border-solid border relative overflow-hidden table-button transition-all duration-200 ${'bg-[#131f2e] border-[#4a5463]  hover:bg-[#1c2a3b] hover:border-[#5a6973] active:bg-[#0f1b28] focus:outline-none focus:ring-2 focus:ring-[#60bfda] focus:ring-opacity-50'}`}
            >
              <span className='ico-add' />
              <span className="h-[20px] shrink-0 basis-auto font-['Mulish'] text-[14px] font-bold leading-[20px] text-[#f7f9f9] relative text-left whitespace-nowrap">
                Add new downtime
              </span>
            </button>
          </Tooltip>
        )}
        {isGLOrEsp && (
          <button
            className={`flex w-auto h-[40px] pt-[10px] pr-[16px] pb-[10px] pl-[16px] gap-[8px] items-center shrink-0 flex-nowrap rounded-[8px] border-solid border relative overflow-hidden table-button transition-all duration-200 ${'bg-[#131f2e] border-[#4a5463]  hover:bg-[#1c2a3b] hover:border-[#5a6973] active:bg-[#0f1b28] focus:outline-none focus:ring-2 focus:ring-[#60bfda] focus:ring-opacity-50'}`}
            onClick={onAddClick}
          >
            <span className='ico-add' />
            <span className="h-[20px] shrink-0 basis-auto font-['Mulish'] text-[14px] font-bold leading-[20px] text-[#f7f9f9] relative text-left whitespace-nowrap">
              Add new downtime
            </span>
          </button>
        )}
        <button
          className='flex w-[118px] h-[40px] pt-[10px] pr-[16px] pb-[10px] pl-[16px] gap-[8px] items-center bg-[#131f2e] rounded-[8px] border border-[#4a5463] table-button ml-2 hover:bg-[#1c2a3b] hover:border-[#5a6973]'
          onClick={() => setShowColumnSelector(true)}
          type='button'
        >
          <span className='ico-colom' />
          <span style={{ fontWeight: 600 }}>Columns</span>
        </button>
        <div className='downtime-export-button'>
          <ExportTable
            data={tableRowsForExport}
            fileName={'Detail Daily Downtime'}
            sheetName={'Detail Daily Downtime'}
            columns={exportColumns}
            disabled={!tableRowsForExport || tableRowsForExport.length === 0}
            rPermission={rPermission}
          />
        </div>
        <TableSearch containerClasses='ml-auto !w-[290px]' onSearch={(searchText) => setSearchTerm(searchText)} />
      </div>
      <div className='detailed-daily-table-container'>
        <DataTable
          data={data}
          searchTerm={searchTerm}
          columns={[...fixedColumnDefs, ...visibleColumns.map((c) => columnDefs.find((cd) => cd.accessor === c))]}
        />
      </div>
    </>
  );
};
export default DetailedDailyTable;
